import { useCallback, useEffect } from 'react';
import { Share } from '@capacitor/share';
import {
  Box,
  Button,
  Card,
  Icon,
  Link,
  Spinner,
  Text,
  Title,
} from '@nimbus-ds/components';
import {
  CheckCircleIcon,
  ChevronLeftIcon,
  ExclamationCircleIcon,
  PrinterIcon,
} from '@nimbus-ds/icons';
import { NuvemEnvioReadyLabelDto } from '@tiendanube/common';
import { Stack } from '@tiendanube/components';
import { useNavegate } from 'App/hooks';
import { useToast } from 'commons/hooks';
import { useIsMobileDevice } from 'domains/Auth/hooks';
import useNuvemEnvioTranslations from 'domains/NuvemEnvioCommons/hooks/useNuvemEnvioTranslations/useNuvemEnvioTranslations';
import useDownloadNuvemEnvioLabel from 'domains/NuvemEnvioCommons/Orders/hooks/useDownloadNuvemEnvioLabel/useDownloadNuvemEnvioLabel';
import { trackingNuvemEnvioPrintDocumentsGeneratedPrintLabelsClick } from 'domains/NuvemEnvioCommons/tracking';

interface PrintLabelResultProps {
  type: 'success' | 'error';
  labels?: NuvemEnvioReadyLabelDto[] | null;
  returnPath?: string;
  orderId?: string;
}

function PrintLabelResult({
  type,
  labels,
  returnPath,
  orderId,
}: Readonly<PrintLabelResultProps>) {
  const t = useNuvemEnvioTranslations();
  const isMobile = useIsMobileDevice();
  const { goTo } = useNavegate();
  const { addToast } = useToast();

  const shouldAlignCenter = (isMobile: boolean, type: 'success' | 'error') => {
    if (isMobile) {
      return type === 'error' ? 'center' : 'flex-start';
    }
    return 'center';
  };

  const shouldJustifySpaceBetween = (
    isMobile: boolean,
    type: 'success' | 'error',
  ) => {
    if (isMobile) {
      return type === 'error' ? 'space-between' : 'flex-start';
    }
    return 'space-between';
  };

  const shouldColumn = (isMobile: boolean, type: 'success' | 'error') => {
    if (isMobile) {
      return type === 'error' ? false : true;
    }
    return false;
  };

  const {
    downloadNuvemEnvioLabel,
    data: downloadNuvemEnvioLabelData,
    status: downloadNuvemEnvioLabelStatus,
  } = useDownloadNuvemEnvioLabel();

  const handlePrintLabel = useCallback(async () => {
    try {
      trackingNuvemEnvioPrintDocumentsGeneratedPrintLabelsClick(
        orderId as string,
      );
      await downloadNuvemEnvioLabel({
        labels: labels as NuvemEnvioReadyLabelDto[],
      });
    } catch (error) {
      addToast({
        label: t(
          'printNuvemEnvioLabelsStatus.asyncPage.printLabelCard.success.toast.error',
        ),
        appearance: 'danger',
      });
    }
  }, [downloadNuvemEnvioLabel, labels, addToast, t, orderId]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'p') {
        e.preventDefault();
        handlePrintLabel();
      }
    };

    globalThis.addEventListener('keydown', handleKeyDown);
    return () => {
      globalThis.removeEventListener('keydown', handleKeyDown);
    };
  }, [handlePrintLabel, labels]);

  useEffect(() => {
    if (isMobile) {
      Share.share({ url: downloadNuvemEnvioLabelData.documentUrl });
    }
  }, [isMobile, downloadNuvemEnvioLabelData]);

  const config = {
    success: {
      icon: <CheckCircleIcon size="medium" color="green" />,
      title: t(
        'printNuvemEnvioLabelsStatus.asyncPage.printLabelCard.success.title',
      ),
      buttonText: t(
        'printNuvemEnvioLabelsStatus.asyncPage.printLabelCard.success.button',
      ),
      buttonIcon: <PrinterIcon />,
      onAction: () => handlePrintLabel(),
      appearance: 'primary' as const,
      dataTestId: 'print-async-nuvem-envio-label-button',
    },
    error: {
      icon: <ExclamationCircleIcon size="medium" color="red" />,
      title: t(
        'printNuvemEnvioLabelsStatus.asyncPage.printLabelCard.error.title',
      ),
      buttonText: t(
        'printNuvemEnvioLabelsStatus.asyncPage.printLabelCard.error.button',
      ),
      buttonIcon: <ChevronLeftIcon />,
      onAction: () => goTo(returnPath as string),
      appearance: 'neutral' as const,
      dataTestId: 'print-async-nuvem-envio-label-error-link',
      textDecoration: 'none' as const,
    },
  }[type];

  return (
    <Card>
      <Stack
        align={shouldAlignCenter(isMobile, type)}
        justify={shouldJustifySpaceBetween(isMobile, type)}
        column={shouldColumn(isMobile, type)}
      >
        <Box display="flex" alignItems="center" gap="2">
          {config.icon}
          <Title as="h5">{config.title}</Title>
        </Box>

        {type === 'success' ? (
          <Button
            appearance={config.appearance}
            onClick={config.onAction}
            data-testid={config.dataTestId}
          >
            {downloadNuvemEnvioLabelStatus.isLoading ? (
              <Spinner size="small" color="currentColor" />
            ) : (
              config.buttonIcon
            )}
            {config.buttonText}
          </Button>
        ) : (
          <Link
            appearance={config.appearance}
            onClick={config.onAction}
            textDecoration={config.textDecoration}
            data-testid={config.dataTestId}
          >
            <Icon color="primary-interactive" source={config.buttonIcon} />
            <Text color="primary-interactive">{config.buttonText}</Text>
          </Link>
        )}
      </Stack>
    </Card>
  );
}

export default PrintLabelResult;
